import { useEffect, useMemo, useState } from 'react'
import { ClipboardCheck, ArrowRight, CheckCircle2, XCircle, Coins } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../lib/shadcn/table'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../lib/shadcn/tooltip'
import { Button } from '../../lib/shadcn/button'
import { Checkbox } from '../../lib/shadcn/checkbox'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { cn } from '../../lib/shadcn/utils'
import { Chip } from '../interviews/Chip'
import {
  useApplyCorrection,
  useBatchApplyCorrections,
  useBatchCloseCorrections,
  usePreviewCorrectionBatch,
} from '../../hooks/backend/shifts'
import { formatDate } from '../../utils/interviews'
import { DenyCorrectionDialog } from './DenyCorrectionDialog'
import { CloseCorrectionDialog } from './CloseCorrectionDialog'
import {
  QueueFilters,
  BASE_FILTERS,
  SEVERITY_FILTERS,
  matchesSeverity,
  type QueueFilter,
  type SeverityFilter,
} from './QueueFilters'
import {
  proposalCategory,
  evidenceTone,
  evidenceLabel,
  type PendingCorrection,
  type ReconcileProposal,
} from './reconcile'
import { BatchActionBar } from './BatchActionBar'
import { BatchApproveDialog } from './BatchApproveDialog'
import { BatchCloseDialog } from './BatchCloseDialog'
import { BatchResultPanel } from './BatchResultPanel'
import {
  isSelectable,
  type BatchApplyResult,
  type BatchCloseResult,
  type BatchPreview,
} from './batch'

// Persistent, verbatim caption. The closing sentence about stale rows is part
// of the caption and must stay attached to it.
const CAPTION =
  "These are proposals, not findings. Most rest on habit inference rather than proof: the driver's other shifts suggest which car they were on. Cartrack corroboration exists for only a handful. Check the rationale before approving, and never approve a material correction without looking at the shift itself. A stale row means the shift was already fixed another way. Deny it."

// Material corrections move money between two owners' statements; minor ones are
// attribution-only (window flips), so keep them calm.
function correctionTone(type: string) {
  return type === 'material' ? 'red' : 'neutral'
}

// Word the change from the field: "window am_pm to pm_am",
// "vehicle MH50CFGP to LK36RJGP".
function changeLabel(field: string | null): string {
  return field ? field : 'value'
}

function matchesFilter(
  _row: PendingCorrection,
  proposal: ReconcileProposal | undefined,
  filter: QueueFilter,
): boolean {
  if (filter === 'all') return true
  return proposalCategory(proposal) === filter
}

type BatchResult =
  | { mode: 'apply'; applyResults: BatchApplyResult[]; closeResults: [] }
  | { mode: 'close'; applyResults: []; closeResults: BatchCloseResult[] }

export function PendingCorrectionsQueue({
  rows,
  proposals,
  filter,
  onFilterChange,
  severity,
  onSeverityChange,
  onApplied,
}: {
  rows: PendingCorrection[]
  proposals: Map<string, ReconcileProposal>
  filter: QueueFilter
  onFilterChange: (next: QueueFilter) => void
  severity: SeverityFilter
  onSeverityChange: (next: SeverityFilter) => void
  // Refresh the pending list and both source queues after a successful apply,
  // deny, or close (single-row or batch).
  onApplied: () => void
}) {
  const { trigger, loading } = useApplyCorrection()
  const [applyingId, setApplyingId] = useState<string | null>(null)

  // Status chip counts are over rows matching the current severity, so the
  // tally reflects what selecting that status would actually show.
  const counts = useMemo(() => {
    const c: Partial<Record<QueueFilter, number>> = {}
    for (const f of BASE_FILTERS) {
      c[f.id] = rows.filter(
        (r) =>
          matchesFilter(r, proposals.get(r.shift_id), f.id) &&
          matchesSeverity(r.correction_type, severity),
      ).length
    }
    return c
  }, [rows, proposals, severity])

  // Severity chip counts are over rows matching the current status filter.
  const severityCounts = useMemo(() => {
    const c: Partial<Record<SeverityFilter, number>> = {}
    for (const f of SEVERITY_FILTERS) {
      c[f.id] = rows.filter(
        (r) =>
          matchesFilter(r, proposals.get(r.shift_id), filter) &&
          matchesSeverity(r.correction_type, f.id),
      ).length
    }
    return c
  }, [rows, proposals, filter])

  // Both filters combine with AND.
  const visible = useMemo(
    () =>
      rows.filter(
        (r) =>
          matchesFilter(r, proposals.get(r.shift_id), filter) &&
          matchesSeverity(r.correction_type, severity),
      ),
    [rows, proposals, filter, severity],
  )

  // --- Batch selection -----------------------------------------------------
  // Only non-stale, non-conflicting rows can be selected. Selection is pruned
  // to the currently visible selectable rows so a stale or filtered-out row can
  // never be submitted.
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const selectableVisibleIds = useMemo(
    () => visible.filter(isSelectable).map((r) => r.id),
    [visible],
  )
  const liveSelected = useMemo(
    () => selectedIds.filter((id) => selectableVisibleIds.includes(id)),
    [selectedIds, selectableVisibleIds],
  )
  const selectedRows = useMemo(
    () => visible.filter((r) => liveSelected.includes(r.id)),
    [visible, liveSelected],
  )

  const allSelected =
    selectableVisibleIds.length > 0 && selectableVisibleIds.every((id) => liveSelected.includes(id))
  const someSelected = liveSelected.length > 0 && !allSelected

  function toggleRow(id: string) {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]))
  }
  function toggleAll() {
    setSelectedIds(allSelected ? [] : selectableVisibleIds)
  }
  function clearSelection() {
    setSelectedIds([])
  }

  // --- Live batch preview --------------------------------------------------
  const previewFn = usePreviewCorrectionBatch()
  const previewTrigger = previewFn.trigger
  const idsKey = liveSelected.join(',')
  useEffect(() => {
    if (idsKey.length === 0) return
    previewTrigger({ correction_ids: idsKey.split(',') }, { skipCache: true })
  }, [idsKey, previewTrigger])
  const preview = (previewFn.data as BatchPreview | null | undefined) ?? null

  // --- Batch actions -------------------------------------------------------
  const batchApply = useBatchApplyCorrections()
  const batchClose = useBatchCloseCorrections()
  const [approveOpen, setApproveOpen] = useState(false)
  const [closeOpen, setCloseOpen] = useState(false)
  const [approveError, setApproveError] = useState<string | null>(null)
  const [closeError, setCloseError] = useState<string | null>(null)
  const [result, setResult] = useState<BatchResult | null>(null)

  async function handleApproveConfirm(notes: string | null) {
    setApproveError(null)
    try {
      const res = await batchApply.trigger(
        { correction_ids: liveSelected, notes },
        { skipCache: true },
      ).result
      setResult({ mode: 'apply', applyResults: (res as BatchApplyResult[]) ?? [], closeResults: [] })
      setApproveOpen(false)
      clearSelection()
      onApplied()
    } catch (err) {
      setApproveError(err instanceof Error ? err.message : String(err))
    }
  }

  async function handleCloseConfirm(reason: string) {
    setCloseError(null)
    try {
      const res = await batchClose.trigger(
        { correction_ids: liveSelected, reason },
        { skipCache: true },
      ).result
      setResult({ mode: 'close', applyResults: [], closeResults: (res as BatchCloseResult[]) ?? [] })
      setCloseOpen(false)
      clearSelection()
      onApplied()
    } catch (err) {
      setCloseError(err instanceof Error ? err.message : String(err))
    }
  }

  // --- Single-row apply (unchanged) ---------------------------------------
  async function handleApply(row: PendingCorrection) {
    setApplyingId(row.id)
    try {
      await trigger({ correction_id: row.id, notes: null }, { skipCache: true }).result
      toast.success('Correction applied.')
      onApplied()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err))
    } finally {
      setApplyingId(null)
    }
  }

  return (
    <div className="space-y-3">
      <p className="max-w-3xl rounded-md border border-amber-300 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
        {CAPTION}
      </p>

      {result ? (
        <BatchResultPanel
          mode={result.mode}
          applyResults={result.applyResults}
          closeResults={result.closeResults}
          onDismiss={() => setResult(null)}
        />
      ) : null}

      <div className="space-y-2">
        <QueueFilters
          filters={BASE_FILTERS}
          value={filter}
          counts={counts}
          onChange={onFilterChange}
        />
        <QueueFilters
          filters={SEVERITY_FILTERS}
          value={severity}
          counts={severityCounts}
          onChange={onSeverityChange}
        />
      </div>

      {visible.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 p-12 text-center text-sm text-muted-foreground">
          <ClipboardCheck className="h-8 w-8 opacity-40" />
          No pending corrections match this filter.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    aria-label="Select all applicable corrections"
                    checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                    disabled={selectableVisibleIds.length === 0}
                    onCheckedChange={toggleAll}
                  />
                </TableHead>
                <TableHead>Shift date</TableHead>
                <TableHead>Driver</TableHead>
                <TableHead>Change</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Evidence</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Proposed by</TableHead>
                <TableHead>Proposed at</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((r) => {
                const busy = loading && applyingId === r.id
                const stale = r.is_stale === true
                const conflict = r.would_conflict === true
                const selectable = isSelectable(r)
                const checked = liveSelected.includes(r.id)
                return (
                  <TableRow key={r.id} data-state={checked ? 'selected' : undefined}>
                    <TableCell className="align-top">
                      {selectable ? (
                        <Checkbox
                          aria-label={`Select correction for ${formatDate(r.shift_date)}`}
                          checked={checked}
                          onCheckedChange={() => toggleRow(r.id)}
                        />
                      ) : (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex">
                                <Checkbox
                                  aria-label="This correction cannot be batch-applied"
                                  checked={false}
                                  disabled
                                />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              {conflict
                                ? 'This would clash on the destination and can only be closed, not applied.'
                                : r.stale_reason ||
                                  'This shift was already fixed another way. It cannot be applied.'}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap align-top font-medium text-foreground">
                      {formatDate(r.shift_date)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap align-top text-sm text-foreground">
                      {r.driver_name || '—'}
                    </TableCell>
                    <TableCell className="align-top">
                      <div className="flex flex-wrap items-center gap-1.5 text-sm text-foreground">
                        <span className="text-muted-foreground">{changeLabel(r.field)}</span>
                        <span className="font-mono">{r.from_value || '—'}</span>
                        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="font-mono">{r.to_value || '—'}</span>
                        {r.changes_owner_earnings === true ? (
                          <Chip tone="amber" icon={<Coins className="h-3 w-3" />}>
                            Owner money
                          </Chip>
                        ) : null}
                      </div>
                      {conflict ? (
                        <div className="mt-1.5 flex items-start gap-1.5">
                          <Chip tone="red">Conflict</Chip>
                          <span className="text-xs leading-relaxed text-muted-foreground">
                            The destination already has a driver in this window that day.
                          </span>
                        </div>
                      ) : stale ? (
                        <div className="mt-1.5 flex items-start gap-1.5">
                          <Chip tone="amber">Stale</Chip>
                          {r.stale_reason ? (
                            <span className="text-xs leading-relaxed text-muted-foreground">
                              {r.stale_reason}
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell className="align-top">
                      <Chip tone={correctionTone(r.correction_type)}>{r.correction_type}</Chip>
                    </TableCell>
                    <TableCell className="align-top">
                      <Chip tone={evidenceTone(r.evidence_grade)}>
                        {evidenceLabel(r.evidence_grade)}
                      </Chip>
                    </TableCell>
                    <TableCell className="max-w-sm align-top text-xs leading-relaxed text-muted-foreground">
                      {r.reason || '—'}
                    </TableCell>
                    <TableCell className="whitespace-nowrap align-top text-sm text-foreground">
                      {r.proposed_by_name || '\u2014'}
                    </TableCell>
                    <TableCell className="whitespace-nowrap align-top text-xs text-muted-foreground">
                      {formatDate(r.proposed_at)}
                    </TableCell>
                    <TableCell className="align-top text-right">
                      <div className="flex items-center justify-end gap-2">
                        {conflict ? (
                          <>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex">
                                    <Button
                                      size="sm"
                                      disabled
                                      className={cn(
                                        'h-7 gap-1.5 px-2 text-xs disabled:opacity-100',
                                        'border border-rose-300 bg-rose-100 text-rose-700 hover:bg-rose-100',
                                        'dark:border-rose-800 dark:bg-rose-950 dark:text-rose-300',
                                      )}
                                    >
                                      <XCircle className="h-3.5 w-3.5" />
                                      Rejected
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  Approving this would move the shift onto a vehicle that already
                                  has a driver in this window that day, creating a clash on the
                                  destination. It cannot be applied, close it instead.
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <CloseCorrectionDialog row={r} onClosed={onApplied} />
                          </>
                        ) : (
                          <>
                            {stale ? (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex">
                                      <Button
                                        size="sm"
                                        className="h-7 gap-1.5 px-2 text-xs"
                                        disabled
                                      >
                                        <CheckCircle2 className="h-3.5 w-3.5" />
                                        Approve and apply
                                      </Button>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    {r.stale_reason ||
                                      'This shift was already fixed another way. Deny it.'}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <Button
                                size="sm"
                                className="h-7 gap-1.5 px-2 text-xs"
                                disabled={busy}
                                onClick={() => handleApply(r)}
                              >
                                {busy ? (
                                  <Spinner className="h-3.5 w-3.5" />
                                ) : (
                                  <CheckCircle2 className="h-3.5 w-3.5" />
                                )}
                                Approve and apply
                              </Button>
                            )}
                            <DenyCorrectionDialog row={r} onDenied={onApplied} />
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {liveSelected.length > 0 ? (
        <BatchActionBar
          count={liveSelected.length}
          preview={preview}
          previewLoading={previewFn.loading}
          onApprove={() => {
            setApproveError(null)
            setApproveOpen(true)
          }}
          onClose={() => {
            setCloseError(null)
            setCloseOpen(true)
          }}
          onClear={clearSelection}
        />
      ) : null}

      <BatchApproveDialog
        open={approveOpen}
        onOpenChange={setApproveOpen}
        rows={selectedRows}
        preview={preview}
        loading={batchApply.loading}
        error={approveError}
        onConfirm={handleApproveConfirm}
      />
      <BatchCloseDialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        rows={selectedRows}
        loading={batchClose.loading}
        error={closeError}
        onConfirm={handleCloseConfirm}
      />
    </div>
  )
}
